// /affiliates — content-driven marketing affiliates landing per spec.
// Source: /content/marketing/affiliates.json (with optional backend
// override at GET /api/marketing/affiliates).
// Routes to /affiliates/apply for the application form.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import { C } from "@/lib/brand";
import { apiGet } from "@/lib/api";
import affiliatesContent from "@/content/marketing/affiliates.json";

export default function MarketingAffiliates() {
  const [content, setContent] = useState(affiliatesContent);

  useEffect(() => {
    // Backend override is optional — the page is fully driven by the
    // local JSON if the BE hasn't shipped the marketing endpoint yet.
    // 404 is expected today and intentionally silent.
    apiGet("/marketing/affiliates", { auth: false })
      .then((backend) => backend && setContent({ ...affiliatesContent, ...backend }))
      .catch((err) => {
        if (err?.status && err.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn("[marketing/affiliates] failed", err);
        }
      });
  }, []);

  const { hero, whyDifferent, howItWorks, compensationDetail, whoWeWorkWith, faqHeader, faq, applicationCta } = content;

  return (
    <div data-testid="marketing-affiliates" style={{ background: C.cream }}>
      <PublicNav />

      {/* Hero */}
      <section style={{ background: C.peach, color: C.peachDark, padding: "104px 24px 96px", position: "relative", overflow: "hidden" }}>
        <span aria-hidden style={{ position: "absolute", top: 24, right: 32, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: "clamp(96px, 16vw, 200px)", lineHeight: 1, opacity: 0.4, color: C.peachDeep, pointerEvents: "none", letterSpacing: "-0.04em" }}>
          ⌖
        </span>
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
          {hero.eyebrow && <Eyebrow color={C.peachDark}>{hero.eyebrow}</Eyebrow>}
          <h1 style={{ marginTop: 22, fontSize: "clamp(44px, 8vw, 92px)", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 0.95, color: C.peachDark, maxWidth: 920 }}>
            <span style={{ display: "block", textTransform: "uppercase" }}>{hero.headlineLine1}</span>
            {hero.headlineLine2 && (
              <span style={{ display: "block", fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontWeight: 400, color: C.ink }}>
                {hero.headlineLine2}
              </span>
            )}
          </h1>
          {hero.subhead && <p style={{ marginTop: 28, fontSize: 17, lineHeight: 1.55, maxWidth: 640, color: C.peachDark, opacity: 0.85 }}>{hero.subhead}</p>}
          {hero.primaryCta && (
            <div style={{ marginTop: 36 }}>
              <Link to={hero.primaryCta.url} data-testid="affiliate-hero-apply" style={{ textDecoration: "none" }}>
                <Button variant="primary" size="lg">{hero.primaryCta.label}</Button>
              </Link>
            </div>
          )}
          {hero.secondaryLine && (
            <div style={{ marginTop: 16, fontSize: 13, color: C.peachDark }}>
              {hero.secondaryLine}
            </div>
          )}
          <div style={{ marginTop: 28, fontSize: 13, color: C.peachDark }}>
            Already approved?{" "}
            <Link
              to="/affiliates/sign-in"
              data-testid="affiliate-hero-signin-link"
              style={{
                color: C.ink,
                fontWeight: 800,
                letterSpacing: "0.16em",
                fontSize: 11,
                textDecoration: "underline",
                textUnderlineOffset: 4,
                marginLeft: 6,
              }}
            >
              SIGN IN TO AFFILIATE PORTAL →
            </Link>
          </div>
        </div>
      </section>

      {/* Why different — 3-up cream */}
      {whyDifferent && (
        <section style={{ background: C.cream, padding: "96px 24px", position: "relative" }}>
          <TopWedge color={C.peach} dir="left" />
          <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
            <Eyebrow color={C.peachDeep}>{whyDifferent.eyebrow}</Eyebrow>
            <Headline before={whyDifferent.headlineLine1} italic={whyDifferent.headlineLine2} size={56} className="mt-4" />
            {whyDifferent.intro && (
              <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.65, color: C.warmGray, maxWidth: 720 }}>
                {whyDifferent.intro}
              </p>
            )}
            <div style={{ marginTop: 56, display: "grid", gap: 28, gridTemplateColumns: "1fr" }} className="md:!grid-cols-3">
              {(whyDifferent.columns || []).map((p, i) => (
                <div key={i} style={{ borderTop: `2px solid ${C.peachDeep}`, paddingTop: 22 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep }}>
                    {String(i + 1).padStart(2, "0")} · {p.title}
                  </div>
                  <p style={{ marginTop: 14, fontSize: 14, lineHeight: 1.65, color: C.warmGray }}>{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How it works — 3 steps on dark */}
      {howItWorks && (
        <section style={{ background: C.ink, color: C.cream, padding: "96px 24px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <Eyebrow color={C.peach}>{howItWorks.eyebrow}</Eyebrow>
            <Headline before={howItWorks.headlineLine1} italic={howItWorks.headlineLine2} color={C.cream} italicColor={C.peach} size={56} className="mt-4" />
            <div style={{ marginTop: 56, display: "grid", gap: 36 }}>
              {(howItWorks.steps || []).map((s, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 24, alignItems: "start" }}>
                  <span aria-hidden style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 64, lineHeight: 1, color: C.peach, opacity: 0.75 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.18em", color: C.peach }}>{s.title}</div>
                    <p style={{ marginTop: 12, fontSize: 16, lineHeight: 1.65, color: "rgba(250,246,240,0.86)" }}>{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Compensation — bone w/ ledger */}
      {compensationDetail && (
        <section style={{ background: C.bone, padding: "96px 24px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <Eyebrow color={C.peachDeep}>{compensationDetail.eyebrow}</Eyebrow>
            <Headline before={compensationDetail.headlineLine1} italic={compensationDetail.headlineLine2} size={56} className="mt-4" />
            {compensationDetail.exampleIntro && (
              <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.65, color: C.warmGray, maxWidth: 720 }}>
                {compensationDetail.exampleIntro}
              </p>
            )}
            <div style={{ marginTop: 32, background: C.pure, padding: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep, marginBottom: 16 }}>★ MONTH 1</div>
              {(compensationDetail.month1Lines || []).map((row, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    padding: "12px 0",
                    borderTop: i === 0 ? "none" : `1px solid ${C.warmGray3}`,
                    fontSize: row.emphasis ? 18 : 14,
                    fontWeight: row.emphasis ? 700 : 500,
                    color: row.emphasis ? C.ink : C.warmGray,
                  }}
                >
                  <span>{row.label}</span>
                  <span style={{ color: row.emphasis ? C.peachDeep : C.ink, fontFamily: row.emphasis ? '"Instrument Serif", Georgia, serif' : "inherit", fontStyle: row.emphasis ? "italic" : "normal" }}>
                    {row.v}
                  </span>
                </div>
              ))}
            </div>
            {compensationDetail.ongoing && (
              <p style={{ marginTop: 28, fontSize: 15, lineHeight: 1.65, color: C.ink, whiteSpace: "pre-line", maxWidth: 720 }}>
                {compensationDetail.ongoing}
              </p>
            )}
            {compensationDetail.footnote && (
              <p style={{ marginTop: 18, fontSize: 12, lineHeight: 1.55, color: C.warmGray, maxWidth: 720 }}>
                {compensationDetail.footnote}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Who we work with — 2-up signals */}
      {whoWeWorkWith && (
        <section style={{ background: C.cream, padding: "96px 24px" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <Eyebrow color={C.peachDeep}>{whoWeWorkWith.eyebrow}</Eyebrow>
            <Headline before={whoWeWorkWith.headlineLine1} italic={whoWeWorkWith.headlineLine2} size={56} className="mt-4" />
            {whoWeWorkWith.body && (
              <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.65, color: C.warmGray, maxWidth: 720 }}>
                {whoWeWorkWith.body}
              </p>
            )}
            <div style={{ marginTop: 48, display: "grid", gap: 28, gridTemplateColumns: "1fr" }} className="md:!grid-cols-2">
              <SignalsCard title="GREAT FITS" items={whoWeWorkWith.fits || []} bg={C.sage} fg={C.sageDark} />
              <SignalsCard title="LESS GOOD FITS" items={whoWeWorkWith.notFits || []} bg={C.peach} fg={C.peachDark} dim />
            </div>
            {whoWeWorkWith.outro && (
              <p style={{ marginTop: 32, fontSize: 16, lineHeight: 1.65, color: C.ink, fontStyle: "italic", fontFamily: '"Instrument Serif", Georgia, serif', maxWidth: 720 }}>
                {whoWeWorkWith.outro}
              </p>
            )}
          </div>
        </section>
      )}

      {/* FAQ */}
      {Array.isArray(faq) && faq.length > 0 && (
        <section style={{ background: C.bone, padding: "96px 24px" }}>
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <Eyebrow color={C.peachDeep}>{(faqHeader && faqHeader.eyebrow) || "★ COMMON QUESTIONS"}</Eyebrow>
            <Headline
              before={(faqHeader && faqHeader.headlineLine1) || "QUESTIONS,"}
              italic={(faqHeader && faqHeader.headlineLine2) || "answered."}
              size={56}
              className="mt-4"
            />
            <div style={{ marginTop: 40, display: "grid", gap: 0 }}>
              {faq.map((q, i) => <FAQItem key={i} item={q} />)}
            </div>
          </div>
        </section>
      )}

      {/* Application CTA */}
      {applicationCta && (
        <section style={{ background: C.ink, color: C.cream, padding: "96px 24px", textAlign: "center" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <Headline before={applicationCta.headlineLine1} italic={applicationCta.headlineLine2} color={C.cream} italicColor={C.peach} size={64} />
            {applicationCta.subhead && (
              <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.6, color: "rgba(250,246,240,0.75)" }}>
                {applicationCta.subhead}
              </p>
            )}
            {applicationCta.cta && (
              <div style={{ marginTop: 32 }}>
                <Link to={applicationCta.cta.url} data-testid="affiliate-final-apply" style={{ textDecoration: "none" }}>
                  <Button variant="primary" size="lg">{applicationCta.cta.label}</Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      <Footer prevColor={C.ink} wedgeDir="right" />
    </div>
  );
}

function SignalsCard({ title, items, bg, fg, dim }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ background: bg, color: fg, padding: 28, opacity: dim ? 0.92 : 1 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>★ {title}</div>
      <ul style={{ margin: "18px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((t, i) => (
          <li key={i} style={{ fontSize: 14, lineHeight: 1.55, paddingLeft: 16, position: "relative" }}>
            <span aria-hidden style={{ position: "absolute", left: 0, top: 0, fontWeight: 800 }}>·</span>
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FAQItem({ item }) {
  return (
    <details className="faq-item" style={{ borderTop: `1px solid ${C.warmGray3}`, padding: "20px 0" }}>
      <summary style={{ cursor: "pointer", listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, fontSize: 16, fontWeight: 700, color: C.ink }}>
        <span style={{ flex: 1 }}>{item.q || item.question}</span>
        <span aria-hidden className="faq-toggle" style={{ fontSize: 20, color: C.peachDeep, transition: "transform 0.2s" }}>+</span>
      </summary>
      <div style={{ marginTop: 12, fontSize: 15, lineHeight: 1.65, color: C.warmGray }}>
        {item.a || item.answer}
      </div>
    </details>
  );
}

